import { useExploreUIState } from 'features/explore/context/use-explore-ui-state';
import { useCallback } from 'react';
import { PlaceOverlay } from 'ui-components/google-map-layers/types/google-map-layers-types';
import { getActiveOverlay } from 'ui-components/google-map-layers/utils/get-place-overlay';
import type { Complex } from '@placer-ui/types';
import {
    isMarketEntity,
    isMarketEntityWithRegionTwins,
    isRegionEntity,
} from 'utils/market-poi/market-poi';

export const useGetOverlayPOI = () => {
    const { overlayPOIs } = useExploreUIState();
    return useCallback(
        (poi: PlaceOverlay): PlaceOverlay[] => {
            return overlayPOIs[poi.id];
        },
        [overlayPOIs],
    );
};

export const useGetActiveOverlay = () => {
    const getOverlayPOI = useGetOverlayPOI();

    return useCallback(
        (poi: PlaceOverlay) => {
            const poiOverlays = getOverlayPOI(poi);
            if (poiOverlays?.length) {
                return getActiveOverlay(poiOverlays, poi.overlayID);
            }
        },
        [getOverlayPOI],
    );
};

type UseValidPoiForCompare = {
    enable: boolean;
    tooltipText?: string;
};

export const marketDisableTooltipText = 'doesn’t support a Property Report';
export const regionDisableTooltipText = 'doesn’t support a Market Report';
/**
 * A custom hook that determines if a POI can be selected for comparison based on
 * the current overlay results and feature flags. It checks the compatibility of
 * regions and markets for comparison and provides tooltip messages when selection is disabled.
 *
 * 1. If the user selects a region first, they cannot add a market without region twins.
 * 2. If the user selects a market first, they cannot add a region.
 *
 * @returns {function} A callback function that takes an entity as a parameter and
 *                     returns an object indicating whether the entity is enabled for
 *                     comparison, along with a tooltip text if it’s disabled.
 *
 */
export const useComparePoiInfo = () => {
    const { selectedOverlayResults: selectedPois } = useExploreUIState();

    return useCallback(
        (compareEntity: Complex): UseValidPoiForCompare => {
            if (!selectedPois.length)
                return {
                    enable: true,
                };

            // The logic based on the first selection poi
            const baseEntityInfo = selectedPois[0];

            if (isMarketEntityWithRegionTwins(baseEntityInfo))
                return {
                    enable: true,
                };

            const isMarketWithoutTwins =
                isMarketEntity(compareEntity) && !isMarketEntityWithRegionTwins(compareEntity);

            if (isRegionEntity(baseEntityInfo) && isMarketWithoutTwins)
                return {
                    enable: false,
                    tooltipText: `${compareEntity.name} ${marketDisableTooltipText}`,
                };

            if (isMarketEntity(baseEntityInfo) && isRegionEntity(compareEntity))
                return {
                    enable: false,
                    tooltipText: `${compareEntity.name} ${regionDisableTooltipText}`,
                };

            return {
                enable: true,
            };
        },
        [selectedPois],
    );
};
